import { observable, configure, action, computed, runInAction } from 'mobx';
import LocalModel from './localModel';

import util from 'preact-util';
import { route } from 'preact-router';
import { v4 as uuidv4 } from 'uuid';
import PubSub, { topics } from '../lib/pubsub';
import { isNetworkErrorEnvelope } from '../lib/offlineCache';
import * as offlineWriteQueue from '../lib/offlineWriteQueue';

configure({ enforceActions: 'always' });

class ArticleStore extends LocalModel {
    constructor() {
        super('article', {
            namePlural: 'articles',
            namePluralReal: 'articles',
            sort: '-sort -publishDate -createdDate',
            limit: 100,

            api: {
                search: {
                    url: '/api/articles/',
                    params: {
                        limit: 100,
                        sort: '-sort -publishDate -createdDate',
                    },
                },
                load: {
                    url: '/api/articles/',
                    params: {},
                },
                save: {
                    url: '/api/articles/',
                    params: {},
                },
                delete: {
                    url: '/api/articles/',
                    params: {},
                },
            },
        });
    }

    // Offline-aware POST (spec: docs/superpowers/specs/2026-08-09-offline-post-creation-design.md).
    // Two triggers: a network failure, or a draft that still carries
    // offline-stashed pending images — those descriptors hold dataURL
    // previews that must NEVER reach a live POST (they would persist
    // server-side and the real files would never upload).
    async insert(data, event) {
        const hasPendingImages = Array.isArray(data && data.images)
            && data.images.some(img => img && img._pendingUpload);
        if (hasPendingImages) {
            return this.queueOfflineInsert(data, { drainNow: true });
        }
        const response = await super.insert(data, event);
        if (isNetworkErrorEnvelope(response)) {
            return this.queueOfflineInsert(data, { fallbackResponse: response });
        }
        return response;
    }

    // Queue the post with a client uuidv4 (the backend dedupes on it) and
    // synthesize a 201 so the composer's create guard passes. drainNow runs
    // the drain immediately — used when we are online but must go through
    // the queue anyway for the image-upload prepare step.
    async queueOfflineInsert(data, { drainNow = false, fallbackResponse = null } = {}) {
        const body = { ...data, uuidv4: uuidv4() };
        const tempId = await offlineWriteQueue.enqueueInsert({
            type: 'article',
            url: '/api/articles/',
            body,
        });
        if (tempId == null) {
            return fallbackResponse || {
                stats: {},
                status: 500,
                message: 'ERROR fetching API: offline queue unavailable',
            };
        }
        PubSub.publish(topics.ARTICLE_PENDING_CHANGED, { tempId });
        if (drainNow) {
            // Lazy require — offlineDrain requires stores at call time.
            // eslint-disable-next-line global-require
            require('../lib/offlineDrain').drainQueues().catch(() => {});
        }
        return {
            status: 201,
            data: { article: { ...body, id: tempId, _pendingSync: true } },
        };
    }

    replacePendingArticle(tempId, realArticle) {
        const articles = (this.articles || []).map(a => (a && a.id === tempId ? { ...realArticle } : a));
        this.updateKeyValue('articles', articles);
        PubSub.publish(topics.ARTICLE_PENDING_CHANGED, { tempId, realId: realArticle && realArticle.id });
    }

    markPendingArticleFailed(tempId, lastError) {
        const articles = (this.articles || []).map(a => (a && a.id === tempId
            ? { ...a, _pendingSync: false, _syncFailed: true, _syncError: lastError || null } : a));
        this.updateKeyValue('articles', articles);
        PubSub.publish(topics.ARTICLE_PENDING_CHANGED, { tempId, failed: true });
    }

    // Domain hostname sent with every article list/save call. Backend routes
    // (get-article.js, post-article.js) resolve it to the domain customer for
    // feed scoping and customer stamping — without it the feed falls back to
    // the viewer's own customer memberships, hiding club posts (incl. the
    // convention photo auto-posts) from non-member users.
    get currentHostname() {
        return typeof window !== 'undefined' ? window.location.hostname : '';
    }

    @observable newArticle = {};

    @observable article = {};

    // State for the AI writing tools above the body editor (bodyAiTools.js).
    @observable aiAssist = { loading: false, action: null, result: null, error: null };

    // «Undersøk dette»: web-researched facts about a marked passage.
    @observable aiResearch = { loading: false, text: '', result: null, error: null };

    @observable articles = [];

    @observable creators = [];

    @observable googleSheet = {};

    @observable tags = [];

    @observable relatedArticles = [];

    @observable factboxArticles = {};

    // Holds the factbox being edited in the editFactbox drawer. Deliberately
    // separate from `article` — that singleton is bound to the (possibly
    // stacked) editArticle drawer and must never be clobbered by nested
    // factbox editing.
    @observable factboxArticle = {};

    @observable aiResponse = {};
    @observable aiResponseObject = {};

    @observable moderationPendingCount = 0;

    @observable videoProcessingArticleId = null;
    @observable videoProcessingStatus = null; // 'processing', 'ready', 'failed'

    @action
    loadModerationPendingCount = async () => {
        try {
            const response = await util.fetchApi('/api/articles/admin/moderation-queue?status=flagged,pending&countOnly=true', { method: 'GET' });
            runInAction(() => {
                this.moderationPendingCount = response?.data?.count || 0;
            });
        } catch (e) {
            // Silently fail - user may not have access
        }
    }

    cleanupAi() {
        this.updateKeyValue('aiResponse', {});
        this.updateKeyValue('aiResponseObject', {});
    }

    async assistAi(payload) {
        this.updateKeyValue('aiAssist', { loading: true, action: payload.action, result: null, error: null });
        const response = await util.fetchApi('/api/ai/article-assist', { method: 'POST' }, payload);
        if (response?.status === 200 && response.data) {
            this.updateKeyValue('aiAssist', { loading: false, action: payload.action, result: response.data, error: null });
        } else {
            this.updateKeyValue('aiAssist', {
                loading: false, action: payload.action, result: null, error: response?.message || 'AI request failed',
            });
        }
        return response;
    }

    async researchAi(payload) {
        this.updateKeyValue('aiResearch', { loading: true, text: payload.text, result: null, error: null });
        const response = await util.fetchApi('/api/ai/article-research', { method: 'POST' }, payload);
        if (response?.status === 200 && response.data) {
            this.updateKeyValue('aiResearch', { loading: false, text: payload.text, result: response.data, error: null });
        } else {
            this.updateKeyValue('aiResearch', {
                loading: false, text: payload.text, result: null, error: response?.message || 'AI research failed',
            });
        }
        return response;
    }

    clearAiResearch() {
        this.updateKeyValue('aiResearch', { loading: false, text: '', result: null, error: null });
    }

    clearAiAssist() {
        this.updateKeyValue('aiAssist', { loading: false, action: null, result: null, error: null });
    }

    // Write an AI suggestion into the article being edited. Existing articles
    // are saved right away; a new article keeps it in newArticle until submit.
    async applyAssistField(objectName, field, value) {
        const obj = this[objectName] || {};
        this.updateObjectKeyValue(objectName, field, value);
        if (objectName !== 'newArticle' && obj.id) {
            await this.saveField(obj.id, field, value, true);
        }
    }

    async queryAi(message, field) {
        this.cleanupAi();
        const response = await util.fetchApi(`/api/ai/article`, { method: 'POST' }, {
            message,
        });
        switch (response?.status) {
            case 200:
                this.updateKeyValue('aiResponse', response?.data);
                let responseObject = null;
                if (response?.data?.output_text) {
                    try {
                        responseObject = JSON.parse(response?.data?.output_text);
                    } catch (e) {
                        // Not JSON, do nothing
                    }
                }
                if (responseObject) {
                    this.updateKeyValue('aiResponseObject', responseObject);
                } else {
                    this.updateKeyValue('aiResponseObject', { [field]: response?.data?.output_text });
                }
                return response?.data;

        }
    }

    // Ask the help-center AI agent for a short answer to a search query.
    // Returns { suggestedAnswer, referencedArticles, confidence } (or null answer on failure).
    async getSearchSuggestion(search) {
        // hostname: the backend scopes the reference articles to the domain customer.
        const response = await util.fetchApi('/api/articles/ai-search', { method: 'POST' }, {
            search,
            hostname: this.currentHostname,
        });
        return response?.data;
    }

    async getGoogleSheet(sheetId) {
        const response = await util.fetchApi(`/api/googlesheets/${sheetId}`, { method: 'GET' }, {});
        switch (response.status) {
            case 200:
                this.updateKeyValue('googleSheet', response.data);
        }
    }

    @action
    async loadRelatedArticles(articleId, options = {}) {
        const { hostname, limit = 5 } = options;
        const query = {};
        if (hostname) {
            query.hostname = hostname;
        }
        if (limit) {
            query.limit = limit;
        }

        try {
            const response = await util.fetchApi(`/api/articles/${articleId}/related`, { method: 'GET' }, query);
            if (response.status === 200) {
                runInAction(() => {
                    this.relatedArticles = response.data || [];
                });
                return response.data;
            }
        } catch (e) {
            console.error('Failed to load related articles:', e.message);
        }
        return [];
    }

    @action
    clearRelatedArticles() {
        this.relatedArticles = [];
    }

    async loadFactboxArticle(articleId) {
        if (!articleId) return;
        // Don't reload if already cached
        const existing = this.factboxArticles[articleId];
        if (existing) return;

        try {
            const response = await util.fetchApi(`/api/articles/${articleId}`, { method: 'GET' }, {});
            if (response?.status === 200) {
                const articleData = Array.isArray(response.data) ? response.data[0] : response.data;
                if (articleData) {
                    const updated = { ...this.factboxArticles, [articleId]: articleData };
                    this.updateKeyValue('factboxArticles', updated);
                }
            }
        } catch (e) {
            console.error('Failed to load factbox article:', e.message);
        }
    }

    async loadFactboxForEdit(articleId) {
        if (!articleId) return;
        this.updateKeyValue('factboxArticle', {});
        try {
            const response = await util.fetchApi(`/api/articles/${articleId}`, { method: 'GET' }, {
                hostname: this.currentHostname,
                all: 1,
            });
            if (response?.status === 200) {
                const articleData = Array.isArray(response.data) ? response.data[0] : response.data;
                if (articleData) {
                    this.updateKeyValue('factboxArticle', articleData);
                }
            }
        } catch (e) {
            console.error('Failed to load factbox article for edit:', e.message);
        }
    }

    // After editing, push the edited factbox into the factboxArticles viewer
    // cache so already-rendered FactBox components pick up the changes.
    refreshFactboxCache(articleId) {
        const articleData = this.factboxArticle;
        if (!articleId || !articleData?.id || articleData.id !== articleId) return;
        this.updateKeyValue('factboxArticles', { ...this.factboxArticles, [articleId]: articleData });
    }

    async generateTldr(articleId) {
        if (!articleId) return null;
        try {
            const response = await util.fetchApi('/api/articles/tldr', { method: 'POST' }, { id: articleId });
            if (response?.status === 200 && response.data?.tldr) {
                // Update the current article observable if it matches
                if (this.article && this.article.id === articleId) {
                    this.updateKeyValue('article', { ...this.article, tldr: response.data.tldr });
                }
                return response.data.tldr;
            }
        } catch (e) {
            console.error('Failed to generate TL;DR:', e.message);
        }
        return null;
    }

    pollVideoStatus(articleId) {
        this.stopVideoPolling();
        this.updateKeyValue('videoProcessingArticleId', articleId);
        this.updateKeyValue('videoProcessingStatus', 'processing');

        const POLL_INTERVAL = 5000; // 5 seconds
        const POLL_TIMEOUT = 15 * 60 * 1000; // 15 minutes
        const startTime = Date.now();

        this._videoPollingTimer = setInterval(async () => {
            if (Date.now() - startTime > POLL_TIMEOUT) {
                this.updateKeyValue('videoProcessingStatus', 'failed');
                this.stopVideoPolling();
                return;
            }

            try {
                const response = await util.fetchApi(`/api/articles/${articleId}`, { method: 'GET' }, {
                    all: 1,
                    userOnly: true,
                });
                if (response?.status === 200) {
                    const articleData = Array.isArray(response.data) ? response.data[0] : response.data;
                    if (articleData && articleData.videoStatus) {
                        if (articleData.videoStatus === 'ready') {
                            this.updateKeyValue('videoProcessingStatus', 'ready');
                            this.stopVideoPolling();
                            this.runRefreshPageFunctions();
                            // Auto-dismiss success after 5 seconds
                            setTimeout(() => {
                                this.updateKeyValue('videoProcessingStatus', null);
                                this.updateKeyValue('videoProcessingArticleId', null);
                            }, 5000);
                        } else if (articleData.videoStatus === 'failed') {
                            this.updateKeyValue('videoProcessingStatus', 'failed');
                            this.stopVideoPolling();
                        }
                    }
                }
            } catch (e) {
                console.error('Video status poll error:', e.message);
            }
        }, POLL_INTERVAL);
    }

    stopVideoPolling() {
        if (this._videoPollingTimer) {
            clearInterval(this._videoPollingTimer);
            this._videoPollingTimer = null;
        }
    }
}

const store = new ArticleStore();
export default store;
